// File: frontend/src/pages/News/NewsV2/NewsV2.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSEO } from '../../contexts/SEOContext';
import { Helmet } from 'react-helmet-async';
import { Search, Calendar, Clock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import LandingButton from '../../components/Button/LandingButton';
import newsArticles from '../../data/newsArticles';
import styles from './NewsV2.module.css';

const NewsV2 = () => {
  const { t, language, supportedLanguages } = useLanguage();
  const { updateSEO } = useSEO();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef(null);
  const articlesPerPage = 6;

  // Get featured article
  const featuredArticle = useMemo(() => 
    newsArticles.find(article => article.featured), 
    []
  );

  // Get all unique categories
  const categories = useMemo(() => {
    const allCategories = new Set();
    allCategories.add('All');
    
    newsArticles.forEach(article => {
      article.categories.forEach(category => {
        allCategories.add(category);
      });
    });
    
    return Array.from(allCategories);
  }, []);

  // Filter articles based on search term and category
  const filteredArticles = useMemo(() => {
    return newsArticles.filter(article => {
      const matchesSearch = 
        article.blog_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.blog_excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.blog_keywords.some(keyword => 
          keyword.toLowerCase().includes(searchTerm.toLowerCase())
        );
      
      const matchesCategory = 
        selectedCategory === 'All' || 
        article.categories.includes(selectedCategory);
      
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory]);

  // Pagination logic
  const indexOfLastArticle = currentPage * articlesPerPage;
  const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
  const currentArticles = filteredArticles.slice(indexOfFirstArticle, indexOfLastArticle);
  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  // SEO setup
  useEffect(() => {
    const baseUrl = 'https://allegory.app';
    const currentPath = '/news';
    
    updateSEO({
      title: t('news_page_title'),
      description: t('news_page_description'),
      keywords: t('news_page_keywords'),
      canonicalUrl: `${baseUrl}/${language}${currentPath}`,
      alternateUrls: supportedLanguages.map(lang => ({
        hreflang: lang,
        href: `${baseUrl}/${lang}${currentPath}`
      })),
      ogImage: `${baseUrl}/images/news-og.jpg`
    });
  }, [t, language, supportedLanguages, updateSEO]);

  // Handle search input
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle category selection
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  // Handle pagination
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(language, options);
  };

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{t('news_page_title')} | Allegory</title>
        <meta name="description" content={t('news_page_description')} />
        <meta name="keywords" content={t('news_page_keywords')} />
        
        {/* Canonical and alternate language links */}
        <link rel="canonical" href={`https://allegory.app/${language}/news`} />
        {supportedLanguages.map(lang => (
          <link 
            key={lang}
            rel="alternate" 
            hreflang={lang} 
            href={`https://allegory.app/${lang}/news`}
          />
        ))}
        <link 
          rel="alternate" 
          hreflang="x-default" 
          href="https://allegory.app/en-ca/news" 
        />

        {/* Open Graph tags */}
        <meta property="og:title" content={t('news_page_title')} />
        <meta property="og:description" content={t('news_page_description')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://allegory.app/${language}/news`} />
        <meta property="og:locale" content={language} />
      </Helmet>

      <div className={styles.wrapper}>
        <div className={styles.container}>
          {/* Hero Section */}
          <motion.div 
            className={styles.hero}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className={styles.title}>{t('news_title')}</h1>
            <p className={styles.subtitle}>{t('news_subtitle')}</p>
            
            {/* Search Bar */}
            <div className={styles.searchContainer}>
              <div 
                className={`${styles.searchBar} ${isSearchFocused ? styles.searchBarFocused : ''}`}
              >
                <Search className={styles.searchIcon} size={20} />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={t('search_news_placeholder')}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                  className={styles.searchInput}
                />
              </div>
            </div>
          </motion.div>

          {/* Featured Article */}
          {featuredArticle && (
            <section className={styles.featuredSection}>
              <h2 className={styles.sectionTitle}>{t('featured_news')}</h2>
              <motion.div 
                className={styles.featuredArticle}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                <div className={styles.featuredImageContainer}>
                  <img 
                    src={`/images/${featuredArticle.image}`} 
                    alt={featuredArticle.blog_title} 
                    className={styles.featuredImage}
                  />
                  <div className={styles.featuredOverlay} />
                </div>
                <div className={styles.featuredContent}>
                  <div className={styles.featuredMeta}>
                    <span className={styles.featuredCategory}>
                      {featuredArticle.categories[0]}
                    </span>
                    <span className={styles.featuredDate}>
                      <Calendar size={14} />
                      {formatDate(featuredArticle.published_at)}
                    </span>
                    <span className={styles.featuredReadTime}>
                      <Clock size={14} />
                      {featuredArticle.blog_time_to_read} min read
                    </span>
                  </div>
                  <h3 className={styles.featuredTitle}>{featuredArticle.blog_title}</h3>
                  <p className={styles.featuredExcerpt}>{featuredArticle.blog_excerpt}</p>
                  <Link to={`/news/${featuredArticle.slug}`} className={styles.featuredLink}>
                    {t('read_more')}
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.div>
            </section>
          )}

          {/* Category Filters */}
          <section className={styles.categorySection}>
            <div className={styles.categoryFilters}>
              {categories.map((category, index) => (
                <button
                  key={category}
                  className={`${styles.categoryButton} ${selectedCategory === category ? styles.activeCategory : ''}`}
                  onClick={() => handleCategoryChange(category)}
                >
                  {category}
                  {selectedCategory === category && (
                    <motion.div 
                      className={styles.categoryIndicator}
                      layoutId="categoryIndicator"
                    />
                  )}
                </button>
              ))}
            </div>
          </section>

          {/* Articles Grid */}
          <section className={styles.articlesSection}>
            <div className={styles.articlesGrid}>
              <AnimatePresence mode="wait">
                {currentArticles.length > 0 ? (
                  currentArticles.map((article, index) => (
                    <motion.div
                      key={article.id}
                      className={styles.articleCard}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                      whileHover={{ y: -5, boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)' }}
                    >
                      <Link to={`/news/${article.slug}`} className={styles.articleLink}>
                        <div className={styles.articleImageContainer}>
                          <img 
                            src={`/images/${article.image}`} 
                            alt={article.blog_title} 
                            className={styles.articleImage}
                          />
                          <div className={styles.articleOverlay} />
                        </div>
                        <div className={styles.articleContent}>
                          <div className={styles.articleMeta}>
                            <span className={styles.articleCategory}>
                              {article.categories[0]}
                            </span>
                            <span className={styles.articleDate}>
                              <Calendar size={12} />
                              {formatDate(article.published_at)}
                            </span>
                            <span className={styles.articleReadTime}>
                              <Clock size={12} />
                              {article.blog_time_to_read} min read
                            </span>
                          </div>
                          <h3 className={styles.articleTitle}>{article.blog_title}</h3>
                          <p className={styles.articleExcerpt}>{article.blog_excerpt}</p>
                          <span className={styles.readMoreLink}>
                            {t('read_more')}
                            <ArrowRight size={14} />
                          </span>
                        </div>
                      </Link>
                    </motion.div>
                  ))
                ) : (
                  <motion.div 
                    className={styles.noResults}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <h3>{t('no_results_title')}</h3>
                    <p>{t('no_results_message')}</p>
                    <button 
                      className={styles.resetButton}
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedCategory('All');
                        if (searchInputRef.current) {
                          searchInputRef.current.focus();
                        }
                      }}
                    >
                      {t('reset_filters')}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  className={`${styles.pageButton} ${currentPage === 1 ? styles.disabled : ''}`}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ArrowRight size={16} className={styles.prevIcon} />
                  {t('previous')}
                </button>
                
                <div className={styles.pageNumbers}>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                    <button
                      key={number}
                      className={`${styles.pageNumber} ${currentPage === number ? styles.activePage : ''}`}
                      onClick={() => handlePageChange(number)}
                    >
                      {number}
                    </button>
                  ))}
                </div>
                
                <button
                  className={`${styles.pageButton} ${currentPage === totalPages ? styles.disabled : ''}`}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  {t('next')}
                  <ArrowRight size={16} />
                </button>
              </div>
            )}
          </section>

          {/* Contact Section */}
          <section className={styles.contactSection}>
            <div className={styles.contactContent}>
              <h2 className={styles.contactTitle}>{t('inquiry_section_title')}</h2>
              <p className={styles.contactDescription}>{t('inquiry_section_description')}</p>
              
              <div className={styles.contactDetails}>
                <div className={styles.contactEmails}>
                  <div className={styles.emailItem}>
                    <h3>{t('ai_inquiry')}</h3>
                    <a href="mailto:ai@allegory.app">ai@allegory.app</a>
                  </div>
                  <div className={styles.emailItem}>
                    <h3>{t('support_inquiry')}</h3>
                    <a href="mailto:support@allegory.app">support@allegory.app</a>
                  </div>
                  <div className={styles.emailItem}>
                    <h3>{t('press_inquiry')}</h3>
                    <a href="mailto:press@allegory.app">press@allegory.app</a>
                  </div>
                </div>
                
                <div className={styles.contactSocial}>
                  <h3>{t('follow_us')}</h3>
                  <div className={styles.socialLinks}>
                    <a href="https://www.linkedin.com/company/allegoryapp" target="_blank" rel="noopener noreferrer" className={styles.socialLink}>LinkedIn</a>
                    <a href="https://www.x.com/allegory_app" target="_blank" rel="noopener noreferrer" className={styles.socialLink}>Twitter</a>
                    <a href="https://www.facebook.com/Allegory-Insurance-115129876917282" target="_blank" rel="noopener noreferrer" className={styles.socialLink}>Facebook</a>
                  </div>
                </div>
              </div>
              
              <div className={styles.contactCta}>
                <LandingButton 
                  href="/contact" 
                  className={styles.contactButton}
                >
                  {t('contact_us')}
                </LandingButton>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default NewsV2;